import type {
  AnalysisBlockerChain,
  AnalysisDriftEvent,
  AnalysisHealthSubScores,
  AnalysisRecommendation,
  AnalysisReadinessRow,
  AnalysisSnapshot,
  AnalysisTabKey,
  AnalysisTrend,
} from '../../../types/analysis'
import {
  type BlockerGraphData,
  type ChartDescriptor,
  type ChartKind,
  type ChartRecord,
  type ReadinessMatrixData,
  type ScatterChartData,
  type ScoreRingChartData,
  type SparklineChartData,
  type SubScoreBarsData,
} from '../charts'

const SUB_SCORE_LABELS: Record<keyof AnalysisHealthSubScores, string> = {
  execution: 'Execution',
  blocker: 'Blocker',
  drift: 'Drift',
  ops: 'Ops',
  ai: 'AI',
}

const sanitizeId = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const snapshotDate = (snapshot: AnalysisSnapshot) => snapshot.generatedAt.toISOString().slice(0, 10)

const withCommon = <TData>({
  snapshot,
  id,
  kind,
  title,
  subtitle,
  tab,
  cardLabel,
  data,
  records,
  onNavigate,
  onOpenReports,
}: {
  snapshot: AnalysisSnapshot
  id: string
  kind: ChartKind
  title: string
  subtitle?: string
  tab: AnalysisTabKey
  cardLabel: string
  data: TData
  records: ChartRecord[]
  onNavigate?: (target: AnalysisRecommendation['drillTarget']) => void
  onOpenReports?: () => void
}): ChartDescriptor<TData> => {
  // Keep the common builder's snapshot-shaped call contract while descriptors
  // derive their records before reaching this shared formatter.
  void snapshot
  return {
  id,
  kind,
  title,
  subtitle,
  source: { tab, cardLabel },
  data,
  buildRecords: () => records,
  onDrillRecord: onNavigate ? (record) => {
    if (record.drillTarget) onNavigate(record.drillTarget)
  } : undefined,
  onOpenReports,
  }
}

export const buildScoreRingDescriptor = ({
  snapshot,
  onNavigate,
  onOpenReports,
}: {
  snapshot: AnalysisSnapshot
  onNavigate: (target: AnalysisRecommendation['drillTarget']) => void
  onOpenReports: () => void
}) => {
  const driftRecords = snapshot.driftEvents.slice(0, 10).map((event): ChartRecord => ({
    id: event.id,
    primary: event.title,
    secondary: event.severity,
    numeric: event.driftDays,
    drillTarget: 'DASHBOARD',
    meta: { plannedEnd: event.plannedEnd },
  }))
  const blockerRecords = snapshot.blockerChains.slice(0, 10).map((chain): ChartRecord => ({
    id: chain.id,
    primary: chain.title,
    secondary: 'Blocked',
    numeric: chain.chainLength,
    drillTarget: 'CHECKLISTS',
    meta: { reason: chain.reason },
  }))
  const records = [...blockerRecords, ...driftRecords].slice(0, 20)
  const data: ScoreRingChartData = {
    score: snapshot.health.score,
    state: snapshot.health.state,
    subScores: (Object.entries(snapshot.health.subScores) as Array<[keyof AnalysisHealthSubScores, number]>)
      .map(([label, value]) => ({ label: SUB_SCORE_LABELS[label], value })),
  }
  return withCommon({
    snapshot,
    id: `score-ring-${snapshot.workspaceId}`,
    kind: 'score-ring',
    title: 'Workspace health score',
    subtitle: `${snapshot.health.state} · ${snapshot.work.active} active items`,
    tab: 'OVERVIEW',
    cardLabel: 'Workspace health',
    data,
    records,
    onNavigate,
    onOpenReports,
  })
}

export const buildSubScoreDescriptor = (snapshot: AnalysisSnapshot, onOpenReports: () => void) => {
  const data: SubScoreBarsData = {
    bars: (Object.entries(snapshot.health.subScores) as Array<[keyof AnalysisHealthSubScores, number]>)
      .map(([label, value]) => ({ label: SUB_SCORE_LABELS[label], value })),
  }
  const records = data.bars.map((bar): ChartRecord => ({
    id: `sub-${sanitizeId(bar.label)}`,
    primary: bar.label,
    secondary: 'Sub-score',
    numeric: bar.value,
    drillTarget: null,
  }))
  return withCommon({
    snapshot,
    id: `sub-scores-${snapshot.workspaceId}`,
    kind: 'sub-score-bars',
    title: 'Health sub-scores',
    subtitle: `Generated ${snapshotDate(snapshot)}`,
    tab: 'OVERVIEW',
    cardLabel: 'Workspace health',
    data,
    records,
    onOpenReports,
  })
}

export const buildTrendDescriptor = ({
  snapshot,
  trend,
  onOpenReports,
}: {
  snapshot: AnalysisSnapshot
  trend: AnalysisTrend
  onOpenReports: () => void
}) => {
  const data: SparklineChartData = {
    label: trend.label,
    points: trend.points,
    direction: trend.direction,
  }
  const records: ChartRecord[] = [{
    id: trend.id,
    primary: trend.label,
    secondary: trend.direction === 'up-is-good' ? 'Activity signal' : 'Pressure signal',
    numeric: trend.current,
    drillTarget: trend.id === 'overdue' ? 'DASHBOARD' : trend.id === 'blocked' ? 'CHECKLISTS' : null,
    meta: { delta: trend.delta },
  }]
  return withCommon({
    snapshot,
    id: `trend-${trend.id}`,
    kind: 'sparkline',
    title: `${trend.label} trend`,
    subtitle: `${trend.current} current · ${trend.delta >= 0 ? '+' : ''}${trend.delta} delta`,
    tab: 'OVERVIEW',
    cardLabel: trend.label,
    data,
    records,
    onOpenReports,
  })
}

export const buildMetricDescriptor = ({
  snapshot,
  tab,
  title,
  value,
  detail,
  points,
  drillTarget,
  onNavigate,
  onOpenReports,
}: {
  snapshot: AnalysisSnapshot
  tab: AnalysisTabKey
  title: string
  value: number
  detail: string
  points?: number[]
  drillTarget?: AnalysisRecommendation['drillTarget'] | null
  onNavigate?: (target: AnalysisRecommendation['drillTarget']) => void
  onOpenReports: () => void
}) => {
  const data: SparklineChartData = {
    label: title,
    points: points?.length ? points : [0, Math.max(0, Math.round(value * 0.35)), Math.max(0, Math.round(value * 0.72)), value],
  }
  const records: ChartRecord[] = [{
    id: `metric-${sanitizeId(title)}`,
    primary: title,
    secondary: detail,
    numeric: value,
    drillTarget: drillTarget ?? null,
  }]
  return withCommon({
    snapshot,
    id: `metric-${sanitizeId(tab)}-${sanitizeId(title)}`,
    kind: 'metric-history',
    title,
    subtitle: detail,
    tab,
    cardLabel: title,
    data,
    records,
    onNavigate,
    onOpenReports,
  })
}

export const buildBlockerDescriptor = (snapshot: AnalysisSnapshot, onNavigate: (target: AnalysisRecommendation['drillTarget']) => void, onOpenReports: () => void) => {
  const records = snapshot.blockerChains.map((chain): ChartRecord => blockerChainRecord(chain))
  const data: BlockerGraphData = {
    nodes: snapshot.blockerChains.slice(0, 40).map((chain) => ({ id: chain.id, label: chain.title, value: chain.chainLength })),
    links: snapshot.blockerChains.slice(0, 40).flatMap((chain) => chain.affectedNodeIds.slice(0, 2).map((affected) => ({ source: chain.id, target: affected }))),
  }
  data.links.forEach((link) => {
    if (!data.nodes.some((node) => node.id === link.target)) {
      data.nodes.push({ id: link.target, label: link.target, value: 1 })
    }
  })
  return withCommon({
    snapshot,
    id: `blocker-graph-${snapshot.workspaceId}`,
    kind: 'blocker-graph',
    title: 'Blocker intelligence',
    subtitle: `${snapshot.blockerChains.length} chains`,
    tab: 'WORK',
    cardLabel: 'Blocker intelligence',
    data,
    records,
    onNavigate,
    onOpenReports,
  })
}

const blockerChainRecord = (chain: AnalysisBlockerChain): ChartRecord => ({
  id: chain.id,
  primary: chain.title,
  secondary: chain.reason,
  numeric: chain.chainLength,
  drillTarget: 'CHECKLISTS',
})

export const buildDriftDescriptor = (snapshot: AnalysisSnapshot, onNavigate: (target: AnalysisRecommendation['drillTarget']) => void, onOpenReports: () => void) => {
  const points = snapshot.driftEvents.map((event, index) => ({
    id: event.id,
    label: event.title,
    x: event.driftDays,
    y: index + 1,
    value: event.driftDays,
    secondary: event.plannedEnd,
  }))
  const data: ScatterChartData = { xLabel: 'Days past planned end', yLabel: 'Item order', points }
  const records = snapshot.driftEvents.map((event): ChartRecord => driftEventRecord(event))
  return withCommon({
    snapshot,
    id: `drift-scatter-${snapshot.workspaceId}`,
    kind: 'drift-scatter',
    title: 'Timeline drift',
    subtitle: `${snapshot.driftEvents.length} drifted items`,
    tab: 'WORK',
    cardLabel: 'Timeline drift',
    data,
    records,
    onNavigate,
    onOpenReports,
  })
}

const driftEventRecord = (event: AnalysisDriftEvent): ChartRecord => ({
  id: event.id,
  primary: event.title,
  secondary: event.severity,
  numeric: event.driftDays,
  drillTarget: 'DASHBOARD',
  meta: { plannedEnd: event.plannedEnd },
})

export const buildReadinessDescriptor = (snapshot: AnalysisSnapshot, onNavigate: (target: AnalysisRecommendation['drillTarget']) => void, onOpenReports: () => void) => {
  const rows = snapshot.readinessMatrix.map((row) => ({
    id: row.id,
    label: row.title,
    cells: (['dependencies', 'proof', 'stock', 'budget'] as const).map((key) => ({ key, status: row[key] })),
  }))
  const data: ReadinessMatrixData = { rows }
  const records = snapshot.readinessMatrix
    .filter((row) => row.dependencies !== 'ready' || row.proof !== 'ready' || row.stock !== 'ready' || row.budget !== 'ready')
    .map((row): ChartRecord => readinessRowRecord(row))
  return withCommon({
    snapshot,
    id: `readiness-matrix-${snapshot.workspaceId}`,
    kind: 'readiness-matrix',
    title: 'Readiness matrix',
    subtitle: `${rows.length} active items`,
    tab: 'WORK',
    cardLabel: 'Readiness matrix',
    data,
    records,
    onNavigate,
    onOpenReports,
  })
}

const readinessRowRecord = (row: AnalysisReadinessRow): ChartRecord => ({
  id: row.id,
  primary: row.title,
  secondary: 'Warn or blocked gate',
  drillTarget: 'CHECKLISTS',
  meta: {
    dependencies: row.dependencies,
    proof: row.proof,
    stock: row.stock,
    budget: row.budget,
  },
})

export type BuildLiveModuleDescriptorParams = {
  snapshot: AnalysisSnapshot
  id: string
  title: string
  subtitle?: string
  tab: AnalysisTabKey
  cardLabel: string
  points: number[]
  records: ChartRecord[]
  direction?: AnalysisTrend['direction']
  onNavigate?: (target: AnalysisRecommendation['drillTarget']) => void
  onOpenReports?: () => void
}

export const buildLiveModuleDescriptor = ({
  snapshot,
  id,
  title,
  subtitle,
  tab,
  cardLabel,
  points,
  records,
  direction = 'up-is-good',
  onNavigate,
  onOpenReports,
}: BuildLiveModuleDescriptorParams): ChartDescriptor<SparklineChartData> => {
  const data: SparklineChartData = {
    label: title,
    points: points.length ? points : [0],
    direction,
  }
  return withCommon({
    snapshot,
    id,
    kind: 'sparkline',
    title,
    subtitle: subtitle ?? `Generated ${snapshotDate(snapshot)}`,
    tab,
    cardLabel,
    data,
    records,
    onNavigate,
    onOpenReports,
  })
}
